import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getSolutionTree, getCategoryAdvantage } from "@/api/solutionApi";
import { getProducts } from "@/api/productApi";
import { getCases } from "@/api/caseApi";
import { assetUrl } from "@/utils/assetUrl";

interface BlockRef {
  "info-security": React.RefObject<HTMLDivElement | null>;
  "it-infrastructure": React.RefObject<HTMLDivElement | null>;
  "intelligent-building": React.RefObject<HTMLDivElement | null>;
}

interface Props {
  blockRefs: BlockRef;
  activeTab: TabId;
  onTabChange: (id: TabId) => void;
}

// ─── Sub-scene definitions ────────────────────────────────────────────────────

interface SubScene {
  id: string;
  icon: string;
  name: string;
  desc: string;
  tags: string[];
  productCategory: string;
  image: string;
}

const securitySubScenes: SubScene[] = [
  {
    id: "network-security",
    icon: "ri-shield-flash-line",
    name: "网络安全",
    desc: "下一代防火墙、入侵检测防御、DDoS清洗、WAF应用防护，构建多层次网络边界防线，阻断外部威胁于边界之外。",
    tags: ["下一代防火墙", "IDS/IPS", "DDoS防护", "WAF"],
    productCategory: "network-security",
    image: "https://readdy.ai/api/search-image?query=cybersecurity%20network%20protection%20concept%2C%20digital%20shield%20glowing%20blue%20light%2C%20abstract%20circuit%20board%20background%2C%20dark%20navy%20background%20with%20subtle%20grid%20lines%2C%20professional%20enterprise%20security%20visualization%2C%20no%20people%2C%20clean%20minimal%20style&width=700&height=420&seq=sec_network01&orientation=landscape",
  },
  {
    id: "data-security",
    icon: "ri-lock-2-line",
    name: "数据安全",
    desc: "数据分类分级、加密保护、DLP防泄漏、数据库审计，守护核心数据资产全生命周期，满足等保与GDPR合规要求。",
    tags: ["数据分级", "加密保护", "DLP防泄漏", "数据库审计"],
    productCategory: "data-security",
    image: "https://readdy.ai/api/search-image?query=data%20security%20encryption%20concept%2C%20glowing%20lock%20icon%20on%20dark%20background%2C%20abstract%20data%20flow%20visualization%2C%20deep%20navy%20and%20teal%20color%20scheme%2C%20enterprise%20data%20protection%2C%20no%20people%2C%20clean%20professional%20style&width=700&height=420&seq=sec_data01&orientation=landscape",
  },
  {
    id: "cloud-security",
    icon: "ri-cloud-line",
    name: "云上安全",
    desc: "云WAF、云访问安全代理（CASB）、云安全态势管理（CSPM），护航云端业务，实现云上资产全面可视可控。",
    tags: ["云WAF", "CASB", "CSPM", "云资产管控"],
    productCategory: "cloud-security",
    image: "https://readdy.ai/api/search-image?query=cloud%20security%20management%20concept%2C%20abstract%20cloud%20computing%20with%20security%20shield%2C%20dark%20background%20with%20glowing%20teal%20and%20blue%20elements%2C%20enterprise%20cloud%20protection%20visualization%2C%20no%20people%2C%20minimalist%20tech%20style&width=700&height=420&seq=sec_cloud01&orientation=landscape",
  },
  {
    id: "security-service",
    icon: "ri-customer-service-2-line",
    name: "信息安全服务",
    desc: "7×24h安全运营托管（MDR）、专业渗透测试、等保合规咨询，提供从评估到持续运营的全周期安全服务。",
    tags: ["安全运营MDR", "渗透测试", "等保合规", "应急响应"],
    productCategory: "security-service",
    image: "https://readdy.ai/api/search-image?query=cybersecurity%20professional%20service%20team%2C%20security%20operations%20center%20with%20analysts%2C%20enterprise%20security%20consulting%2C%20professional%20dark%20environment%20with%20multiple%20monitors%2C%20security%20experts%20working%2C%20no%20faces%20visible&width=700&height=420&seq=sec_service01&orientation=landscape",
  },
];

const infraSubScenes: SubScene[] = [
  {
    id: "hyperconverged",
    icon: "ri-stack-line",
    name: "超融合解决方案",
    desc: "计算、存储、网络三合一超融合架构，支持VMware/KVM虚拟化，即插即用，线性扩展，大幅简化IT运维复杂度。",
    tags: ["超融合一体机", "虚拟化", "线性扩展", "统一管理"],
    productCategory: "hyperconverged",
    image: "https://readdy.ai/api/search-image?query=hyper-converged%20infrastructure%20HCI%20technology%20concept%2C%20virtualization%20server%20consolidation%2C%20modern%20IT%20architecture%20simplification%2C%20blue%20technology%20visualization%2C%20enterprise%20computing%20storage%20network%20integration&width=700&height=420&seq=infra_hci01&orientation=landscape",
  },
  {
    id: "desktop-cloud",
    icon: "ri-computer-line",
    name: "桌面云解决方案",
    desc: "VDI虚拟桌面基础架构，桌面系统集中部署于数据中心，员工通过任意终端安全接入，数据不落地，安全便捷。",
    tags: ["VDI虚拟桌面", "数据不落地", "集中管控", "多终端接入"],
    productCategory: "desktop-cloud",
    image: "https://readdy.ai/api/search-image?query=virtual%20desktop%20infrastructure%20VDI%20concept%2C%20enterprise%20desktop%20cloud%20solution%2C%20multiple%20thin%20client%20workstations%20connected%20to%20central%20server%2C%20clean%20office%20environment%2C%20modern%20IT%20workspace%20visualization&width=700&height=420&seq=infra_vdi01&orientation=landscape",
  },
  {
    id: "storage",
    icon: "ri-hard-drive-2-line",
    name: "存储解决方案",
    desc: "全闪存储阵列与分布式存储双线并行，覆盖高性能数据库场景与海量非结构化数据存储，满足不同业务需求。",
    tags: ["全闪存储", "分布式存储", "NVMe", "EB级扩展"],
    productCategory: "storage",
    image: "https://readdy.ai/api/search-image?query=enterprise%20storage%20array%20all-flash%20NVMe%20concept%2C%20professional%20data%20storage%20infrastructure%2C%20high%20performance%20storage%20visualization%2C%20clean%20light%20background%2C%20organized%20storage%20hardware%2C%20enterprise%20IT%20storage&width=700&height=420&seq=infra_storage01&orientation=landscape",
  },
  {
    id: "managed-cloud",
    icon: "ri-upload-cloud-2-line",
    name: "托管云解决方案",
    desc: "专属物理服务器托管于T3+级数据中心，配套7×24h专业运维、安全防护与弹性带宽，降低企业IT管理负担。",
    tags: ["专属托管", "T3+数据中心", "7×24h运维", "弹性扩容"],
    productCategory: "managed-cloud",
    image: "https://readdy.ai/api/search-image?query=managed%20cloud%20hosting%20service%20concept%2C%20professional%20data%20center%20server%20hosting%2C%20enterprise%20cloud%20infrastructure%20management%2C%20modern%20data%20center%20with%20organized%20server%20racks%2C%20blue%20LED%20lighting%2C%20managed%20IT%20services%20visualization&width=700&height=420&seq=infra_mc01&orientation=landscape",
  },
  {
    id: "disaster-recovery",
    icon: "ri-refresh-line",
    name: "容灾备份解决方案",
    desc: "同城双活、异地灾备、云端灾备多种模式，统一备份管理平台覆盖物理机/虚拟机/数据库，RPO/RTO双优目标。",
    tags: ["同城双活", "异地灾备", "统一备份", "RPO/RTO"],
    productCategory: "disaster-recovery",
    image: "https://readdy.ai/api/search-image?query=disaster%20recovery%20backup%20data%20center%20concept%2C%20business%20continuity%20planning%20visualization%2C%20dual%20data%20center%20failover%2C%20professional%20IT%20resilience%20infrastructure%2C%20blue%20technology%20background%2C%20enterprise%20backup%20systems&width=700&height=420&seq=infra_dr01&orientation=landscape",
  },
  {
    id: "hybrid-cloud",
    icon: "ri-git-merge-line",
    name: "同架构混合云解决方案",
    desc: "统一管理公有云与私有云资源，工作负载灵活调度，数据安全流转，打破云孤岛，构建一体化混合云架构。",
    tags: ["多云统一管理", "弹性调度", "安全互联", "统一账单"],
    productCategory: "hybrid-cloud",
    image: "https://readdy.ai/api/search-image?query=hybrid%20cloud%20management%20concept%2C%20public%20and%20private%20cloud%20integration%20visualization%2C%20enterprise%20multi-cloud%20architecture%2C%20abstract%20cloud%20network%20diagram%2C%20teal%20and%20dark%20blue%20color%20scheme%2C%20professional%20IT%20cloud%20services&width=700&height=420&seq=infra_hybrid01&orientation=landscape",
  },
  {
    id: "network-infra",
    icon: "ri-router-line",
    name: "网络建设解决方案",
    desc: "SD-WAN广域网优化、园区核心网络建设、数据中心网络架构设计，提供从规划到实施的全栈网络交付服务。",
    tags: ["SD-WAN", "园区网络", "数据中心网络", "全栈交付"],
    productCategory: "network-infra",
    image: "https://readdy.ai/api/search-image?query=enterprise%20network%20infrastructure%20concept%2C%20abstract%20network%20topology%20visualization%2C%20clean%20light%20background%20with%20subtle%20connection%20lines%2C%20professional%20IT%20network%20design%2C%20SD-WAN%20campus%20network%2C%20no%20people%2C%20minimal%20corporate%20style&width=700&height=420&seq=infra_net01&orientation=landscape",
  },
];

const smartSubScenes: SubScene[] = [
  {
    id: "campus-smart",
    icon: "ri-building-4-line",
    name: "园区建设智能化解决方案",
    desc: "综合布线、视频监控、人脸识别门禁、楼宇自控、访客管理一体化集成，打造安全高效的智慧园区运营体系。",
    tags: ["视频监控", "人脸门禁", "综合布线", "楼宇自控"],
    productCategory: "campus-smart",
    image: "https://readdy.ai/api/search-image?query=modern%20smart%20campus%20building%20exterior%20with%20security%20cameras%2C%20intelligent%20building%20management%2C%20professional%20corporate%20park%2C%20clean%20architecture%2C%20daytime%2C%20technology%20integration%2C%20no%20people&width=700&height=420&seq=smart_campus01&orientation=landscape",
  },
  {
    id: "smart-meeting",
    icon: "ri-presentation-line",
    name: "智能化会议解决方案",
    desc: "4K智能会议一体机、无线投屏、会议室预约管理、远程视频协作，打造高效现代化会议体验，提升会议室利用率。",
    tags: ["智能会议一体机", "无线投屏", "会议室预约", "远程协作"],
    productCategory: "smart-meeting",
    image: "https://readdy.ai/api/search-image?query=modern%20smart%20conference%20room%20with%20large%20display%20screens%2C%20video%20conferencing%20system%2C%20professional%20meeting%20room%20technology%2C%20clean%20minimalist%20interior%20design%2C%20no%20people&width=700&height=420&seq=smart_meeting01&orientation=landscape",
  },
  {
    id: "datacenter-build",
    icon: "ri-server-line",
    name: "数据中心建设解决方案",
    desc: "T3+级机房规划设计、精密空调制冷、模块化UPS供配电、综合布线，提供数据中心全生命周期建设服务。",
    tags: ["T3+机房规划", "精密空调", "模块化UPS", "综合布线"],
    productCategory: "datacenter-build",
    image: "https://readdy.ai/api/search-image?query=professional%20data%20center%20server%20room%20with%20organized%20racks%2C%20blue%20LED%20lighting%2C%20precision%20cooling%20systems%2C%20structured%20cabling%2C%20enterprise%20IT%20facility%2C%20no%20people&width=700&height=420&seq=smart_dc01&orientation=landscape",
  },
];

// ─── Tab config ───────────────────────────────────────────────────────────────

const TABS = [
  {
    id: "info-security" as const,
    icon: "ri-shield-keyhole-line",
    label: "信息安全解决方案",
    color: "#1a56db",
    hoverBg: "#eef2ff",
  },
  {
    id: "it-infrastructure" as const,
    icon: "ri-server-line",
    label: "基础架构解决方案",
    color: "#0891b2",
    hoverBg: "#f0f9ff",
  },
  {
    id: "intelligent-building" as const,
    icon: "ri-building-4-line",
    label: "弱电智能化解决方案",
    color: "#059669",
    hoverBg: "#ecfdf5",
  },
];

type TabId = "info-security" | "it-infrastructure" | "intelligent-building";

// ─── Sub-scene scroll row (auto-scroll when >4 items) ──────────────────────

interface ScrollRowProps {
  scenes: SubScene[];
  accentColor: string;
  onNavigate: (category: string) => void;
}

// ─── Sub-scene grid row (show all items) ───────────────────────────────────────

function SubSceneGridRow({ scenes, accentColor, onNavigate }: ScrollRowProps) {
  return (
    <div className="flex flex-wrap justify-center gap-5 mb-8">
      {scenes.map((scene) => (
        <div key={scene.id} className="w-full sm:w-[calc(50%-10px)] lg:w-[calc(33.333%-13.33px)] xl:w-[calc(25%-15px)]">
          <SubSceneCard scene={scene} accentColor={accentColor} onNavigate={onNavigate} />
        </div>
      ))}
    </div>
  );
}

// ─── Sub-scene card ───────────────────────────────────────────────────────────

interface SubSceneCardProps {
  scene: SubScene;
  accentColor: string;
  onNavigate: (category: string) => void;
}

function SubSceneCard({ scene, accentColor, onNavigate }: SubSceneCardProps) {
  return (
    <div
      onClick={() => onNavigate(scene.id)}
      className="group h-full rounded-2xl overflow-hidden border border-white/60 hover:border-white transition-all duration-300 cursor-pointer bg-white/80 backdrop-blur-sm flex flex-col"
      style={{ boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}
    >
      <div className="relative w-full h-44 overflow-hidden flex-shrink-0">
        <img
          src={assetUrl(scene.image)}
          alt={scene.name}
          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0d1f3c]/75 to-transparent"></div>
        <div className="absolute bottom-3 left-4 flex items-center gap-2">
          <div
            className="w-7 h-7 flex items-center justify-center rounded-lg"
            style={{ backgroundColor: `${accentColor}cc` }}
          >
            <i className={`${scene.icon || "ri-shield-line"} text-white text-sm`}></i>
          </div>
          <span className="text-white text-sm font-bold">{scene.name}</span>
        </div>
        <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <div
            className="w-7 h-7 flex items-center justify-center rounded-full"
            style={{ backgroundColor: `${accentColor}dd` }}
          >
            <i className="ri-arrow-right-up-line text-white text-sm"></i>
          </div>
        </div>
      </div>
      <div className="p-5 flex flex-col flex-1">
        <p className="text-xs text-gray-500 leading-relaxed mb-3 line-clamp-2">{scene.desc}</p>
        <div className="flex flex-wrap gap-1.5 mt-auto mb-3 content-start">
          {scene.tags.map((tag) => (
            <span
              key={tag}
              className="px-2 py-0.5 text-xs rounded-full border h-fit"
              style={{
                backgroundColor: `${accentColor}08`,
                color: accentColor,
                borderColor: `${accentColor}25`,
              }}
            >
              {tag}
            </span>
          ))}
        </div>
        <div
          className="flex items-center gap-1.5 text-xs font-semibold group-hover:gap-2.5 transition-all duration-200"
          style={{ color: accentColor }}
        >
          <span>查看方案详情</span>
          <div className="w-3.5 h-3.5 flex items-center justify-center">
            <i className="ri-arrow-right-line text-xs"></i>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Solution Intro Section ───────────────────────────────────────────────────

interface IntroSectionProps {
  accentColor: string;
  title: string;
  desc: string;
  highlights: { icon: string; text: string }[];
  bgImage: string;
  stats: { value: string; label: string }[];
}

function IntroSection({ accentColor, title, desc, highlights, bgImage, stats }: IntroSectionProps) {
  return (
    <div className="relative rounded-3xl overflow-hidden mb-6">
      {/* Background image with overlay */}
      <div className="absolute inset-0">
        <img src={assetUrl(bgImage)} alt="" className="w-full h-full object-cover object-top" />
        <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${accentColor}90 0%, ${accentColor}60 40%, rgba(22,34,54,0.65) 100%)` }}></div>
      </div>
      <div className="relative z-10 p-10 lg:p-14">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          {/* Left: text */}
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-5 border border-white/20 bg-white/10">
              <div className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></div>
              <span className="text-white/80 text-xs font-medium tracking-wider">方案简介</span>
            </div>
            <h3 className="text-2xl lg:text-3xl font-bold text-white mb-4 leading-snug">{title}</h3>
            <p className="text-white/75 text-sm leading-relaxed mb-8">{desc}</p>
            {/* Stats row */}
            <div className="flex flex-wrap gap-6">
              {stats.map((s) => (
                <div key={s.label}>
                  <div className="text-2xl font-extrabold text-white">{s.value}</div>
                  <div className="text-xs text-white/60 mt-0.5">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
          {/* Right: highlights grid */}
          <div className="grid grid-cols-2 gap-3">
            {highlights.map((h) => (
              <div
                key={h.text}
                className="flex items-center gap-3 px-4 py-3.5 rounded-xl bg-white/10 border border-white/15 backdrop-blur-sm hover:bg-white/15 transition-colors duration-200"
              >
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/15 flex-shrink-0">
                  <i className={`${h.icon} text-white text-sm`}></i>
                </div>
                <span className="text-white text-sm font-medium">{h.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Architecture Section ─────────────────────────────────────────────────────

interface ArchItem {
  icon: string;
  name: string;
  desc: string;
  /** 关联产品（从 advantageData.arch 解析得到，前台以灰字链接展示） */
  relatedProducts?: { id: number; name: string }[];
}

interface ArchTier {
  label: string;
  items: ArchItem[];
}

interface ArchSectionProps {
  accentColor: string;
  archTitle: string;
  archTiers: ArchTier[];
}

function ArchSection({ accentColor, archTitle, archTiers }: ArchSectionProps) {
  return (
    <div className="rounded-3xl border border-gray-100 bg-white p-8 lg:p-10 mb-6">
      <div className="flex items-center gap-3 mb-8">
        <div className="w-1 h-6 rounded-full" style={{ backgroundColor: accentColor }}></div>
        <h4 className="text-lg font-bold text-[#0d1f3c]">{archTitle}</h4>
      </div>
      <div className="space-y-6">
        {archTiers.map((tier, ti) => (
          <div key={tier.label} className="relative">
            {/* Tier connector line */}
            {ti < archTiers.length - 1 && (
              <div className="absolute left-5 top-full w-px h-6 z-10" style={{ backgroundColor: `${accentColor}30` }}></div>
            )}
            <div
              className="flex items-center gap-3 px-5 py-3 rounded-xl mb-4 w-fit"
              style={{ backgroundColor: `${accentColor}10`, border: `1px solid ${accentColor}20` }}
            >
              <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: accentColor }}></div>
              <span className="text-sm font-bold" style={{ color: accentColor }}>{tier.label}</span>
            </div>
            <div
              className="grid gap-4"
              style={{ gridTemplateColumns: `repeat(${Math.min(tier.items.length, 3)}, minmax(0, 1fr))` }}
            >
              {tier.items.map((item) => (
                <div
                  key={item.name}
                  className="flex items-start gap-4 p-5 rounded-2xl border transition-all duration-200 hover:border-opacity-60"
                  style={{ borderColor: `${accentColor}18`, backgroundColor: `${accentColor}04` }}
                >
                  <div
                    className="w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0"
                    style={{ backgroundColor: `${accentColor}12` }}
                  >
                    <i className={`${item.icon} text-lg`} style={{ color: accentColor }}></i>
                  </div>
                  <div className="min-w-0 flex-1">
                    <h5 className="text-sm font-bold text-[#0d1f3c] mb-1">{item.name}</h5>
                    <p className="text-xs text-gray-400 leading-relaxed">{item.desc}</p>
                    {item.relatedProducts && item.relatedProducts.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1">
                        {item.relatedProducts.map((p) => (
                          <Link
                            key={p.id}
                            to={`/products/${p.id}`}
                            className="text-xs text-gray-400 hover:text-[color:var(--acc)] transition-colors underline-offset-2 hover:underline"
                            style={{ ["--acc" as any]: accentColor }}
                            title={p.name}
                          >
                            {p.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Advantages Section (BIG) ─────────────────────────────────────────────────

interface AdvantageItem {
  icon: string;
  title: string;
  value: string;
  desc: string;
  detail: string;
  step: string;
}

interface AdvantagesSectionProps {
  accentColor: string;
  sectionTitle: string;
  sectionSubtitle: string;
  items: AdvantageItem[];
  valueSummary: { icon: string; title: string; desc: string }[];
}

function AdvantagesSection({ accentColor, sectionTitle, sectionSubtitle, items, valueSummary }: AdvantagesSectionProps) {
  return (
    <div className="mb-6">
      {/* Big advantages block */}
      <div
        className="rounded-3xl overflow-hidden"
        style={{ background: `linear-gradient(160deg, #2a3a52 0%, #1e2d45 60%, ${accentColor}20 100%)` }}
      >
        {/* Header */}
        <div className="px-10 lg:px-14 pt-12 pb-8 text-center border-b border-white/8">
          <div
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-4 border"
            style={{ borderColor: `${accentColor}40`, backgroundColor: `${accentColor}15` }}
          >
            <i className="ri-star-line text-xs" style={{ color: accentColor }}></i>
            <span className="text-xs font-semibold tracking-wider" style={{ color: accentColor }}>核心优势</span>
          </div>
          <h3 className="text-2xl lg:text-3xl font-bold text-white mb-3">{sectionTitle}</h3>
          <p className="text-white/50 text-sm max-w-lg mx-auto">{sectionSubtitle}</p>
        </div>

        {/* Advantage cards grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/5">
          {items.map((item) => (
            <div
              key={item.title}
              className="group flex flex-col gap-4 px-8 py-10 transition-all duration-300 cursor-default"
              style={{ backgroundColor: "rgba(22,34,54,0.55)" }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.backgroundColor = `${accentColor}25`; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.backgroundColor = "rgba(22,34,54,0.55)"; }}
            >
              <span className="text-xs font-bold tracking-widest" style={{ color: `${accentColor}60` }}>{item.step}</span>
              <div
                className="w-14 h-14 flex items-center justify-center rounded-2xl transition-transform duration-300 group-hover:scale-110"
                style={{ backgroundColor: `${accentColor}20`, border: `1px solid ${accentColor}35` }}
              >
                <i className={`${item.icon} text-2xl`} style={{ color: accentColor }}></i>
              </div>
              <div className="text-3xl font-extrabold text-white">{item.value}</div>
              <div>
                <h5 className="text-base font-bold text-white mb-2">{item.title}</h5>
                <p className="text-sm text-white/55 leading-relaxed">{item.desc}</p>
              </div>
              <div
                className="mt-auto pt-4 border-t text-xs leading-relaxed"
                style={{ borderColor: `${accentColor}35`, color: "rgba(255,255,255,0.65)" }}
              >
                {item.detail}
              </div>
            </div>
          ))}
        </div>

        {/* Value summary row */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-white/5">
          {valueSummary.map((v) => (
            <div
              key={v.title}
              className="flex items-start gap-5 px-10 py-8"
              style={{ backgroundColor: `${accentColor}18` }}
            >
              <div
                className="w-12 h-12 flex items-center justify-center rounded-xl flex-shrink-0"
                style={{ backgroundColor: `${accentColor}20` }}
              >
                <i className={`${v.icon} text-xl`} style={{ color: accentColor }}></i>
              </div>
              <div>
                <h5 className="text-sm font-bold text-white mb-1.5">{v.title}</h5>
                <p className="text-xs text-white/55 leading-relaxed">{v.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Cases row ────────────────────────────────────────────────────────────────

interface CasesRowProps {
  category: string;
  accentColor: string;
}

function CasesRow({ category, accentColor }: CasesRowProps) {
  const cases = caseList.filter((c) => c.solution === category);
  if (cases.length === 0) return null;

  return (
    <div className="mt-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-1 h-5 rounded-full" style={{ backgroundColor: accentColor }}></div>
          <h4 className="text-base font-bold text-[#0d1f3c]">行业标杆案例</h4>
        </div>
        <Link
          to="/cases"
          className="flex items-center gap-1.5 text-sm font-medium hover:gap-2.5 transition-all duration-200 whitespace-nowrap cursor-pointer"
          style={{ color: accentColor }}
        >
          查看全部案例
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-arrow-right-line text-sm"></i>
          </div>
        </Link>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {cases.map((c) => (
          <div
            key={c.id}
            className="bg-white rounded-2xl overflow-hidden border border-gray-100 transition-all duration-300 group cursor-pointer"
          >
            <div className="relative w-full h-40 overflow-hidden">
              <img
                src={assetUrl(c.image)}
                alt={c.title}
                className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0d1f3c]/60 to-transparent"></div>
              <div className="absolute top-3 left-3">
                <span
                  className="px-2.5 py-1 text-white text-xs font-medium rounded-full"
                  style={{ backgroundColor: `${accentColor}dd` }}
                >
                  {c.solutionName}
                </span>
              </div>
            </div>
            <div className="p-5">
              <p className="text-xs text-gray-400 mb-1.5">{c.client}</p>
              <h5 className="text-sm font-bold text-[#0d1f3c] mb-2.5 leading-snug line-clamp-2">{c.title}</h5>
              <p className="text-xs text-gray-500 leading-relaxed mb-4 line-clamp-2">{c.summary}</p>
              <div className="flex gap-4 pt-3 border-t border-gray-50">
                {c.metrics.slice(0, 3).map((m) => (
                  <div key={m.label} className="text-center flex-1">
                    <div className="text-sm font-bold" style={{ color: accentColor }}>{m.value}</div>
                    <div className="text-xs text-gray-400 mt-0.5">{m.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Dynamic Cases Row (from API) ─────────────────────────────────────────────

interface DynamicCasesRowProps {
  cases: any[];
  accentColor: string;
  categoryId: string;
}

function DynamicCasesRow({ cases, accentColor, categoryId }: DynamicCasesRowProps) {
  if (cases.length === 0) return null;
  return (
    <div className="mt-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-1 h-5 rounded-full" style={{ backgroundColor: accentColor }}></div>
          <h4 className="text-base font-bold text-[#0d1f3c]">行业标杆案例</h4>
        </div>
        <Link
          to={`/cases?solution=${categoryId}`}
          className="flex items-center gap-1.5 text-sm font-medium hover:gap-2.5 transition-all duration-200 whitespace-nowrap cursor-pointer"
          style={{ color: accentColor }}
        >
          查看全部案例
          <i className="ri-arrow-right-line text-sm"></i>
        </Link>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {cases.map((c: any) => (
          <Link
            key={c.id}
            to={`/cases/${c.id}`}
            className="bg-white rounded-2xl overflow-hidden border border-gray-100 transition-all duration-300 group cursor-pointer block"
          >
            <div className="relative w-full h-40 overflow-hidden">
              <img src={assetUrl(c.image)} alt={c.title} className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0d1f3c]/60 to-transparent"></div>
              <div className="absolute top-3 left-3">
                <span className="px-2.5 py-1 text-white text-xs font-medium rounded-full" style={{ backgroundColor: `${accentColor}dd` }}>
                  {c.solutionName || c.industry || ""}
                </span>
              </div>
            </div>
            <div className="p-5">
              <p className="text-xs text-gray-400 mb-1.5">{c.client}</p>
              <h5 className="text-sm font-bold text-[#0d1f3c] mb-2.5 leading-snug line-clamp-2">{c.title}</h5>
              <p className="text-xs text-gray-500 leading-relaxed line-clamp-2">{c.summary ?? c.description ?? ""}</p>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

// ─── Panel components ─────────────────────────────────────────────────────────

interface PanelProps {
  onNavigate: (category: string) => void;
  apiSubSolutions: SubScene[];
  apiCases: any[];
  apiProducts: { id: number; name: string }[];
  advantageData: any | null;
}

// 把后端配置的 arch (JSON) 解析为 ArchSection 所需的 tiers + 解析 productIds
function parseArchFromAdvantage(
  advantageData: any | null,
  apiProducts: { id: number; name: string }[],
): { title: string; tiers: ArchTier[] } | null {
  if (!advantageData?.arch) return null;
  let raw = advantageData.arch;
  try {
    if (typeof raw === "string") raw = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!raw || typeof raw !== "object") return null;

  const productMap = new Map(apiProducts.map((p) => [p.id, p.name]));
  const tiersRaw = Array.isArray(raw.tiers) ? raw.tiers : [];
  if (tiersRaw.length === 0) return null;

  const tiers: ArchTier[] = tiersRaw.map((t: any, ti: number) => ({
    label: t.label || `第 ${ti + 1} 层`,
    items: (Array.isArray(t.items) ? t.items : []).map((it: any) => ({
      icon: it.icon || "",
      name: it.name || "",
      desc: it.desc || "",
      relatedProducts: (Array.isArray(it.productIds) ? it.productIds : [])
        .map((pid: any) => Number(pid))
        .filter((pid: number) => !Number.isNaN(pid))
        .map((pid: number) => ({ id: pid, name: productMap.get(pid) || "" }))
        .filter((p: { id: number; name: string }) => !!p.name),
    })),
  }));

  return { title: raw.title || "", tiers };
}

function SecurityPanel({ onNavigate, apiSubSolutions, apiCases, apiProducts, advantageData }: PanelProps) {
  const color = "#1a56db";
  const subs = apiSubSolutions.length > 0 ? apiSubSolutions : securitySubScenes;

  // 从API数据构建AdvantagesSection的props，如果没有API数据则使用默认值
  const advTitle = advantageData?.title || "为什么选择我们的信息安全方案";
  const advSubtitle = advantageData?.subtitle || "专业团队 · 全栈能力 · 持续护航，让安全真正落地";
  const advMainCards = advantageData?.mainCards;
  const advBottomCards = advantageData?.bottomCards;

  const defaultItems = [
    { icon: "ri-verified-badge-line", label: "等保合规", value: "三级认证", title: "等保合规全覆盖", desc: "覆盖等保2.0一至三级全级别，协助企业完成差距分析、整改加固与认证申报，一站式合规交付。", detail: "已协助 200+ 家企业完成等保认证，通过率 100%", step: "01" },
    { icon: "ri-radar-line", label: "主动防御", value: "7×24h", title: "全天候主动防御", desc: "安全运营中心全天候监控，结合威胁情报与AI分析引擎，主动发现并阻断攻击，平均响应时间不超过5分钟。", detail: "年均处理安全事件 10,000+ 起，误报率低于 0.1%", step: "02" },
    { icon: "ri-shield-check-line", label: "威胁拦截率", value: "99.9%", title: "多层协同防御", desc: "网络层、应用层、数据层、终端层四层防御体系协同联动，有效拦截已知与未知威胁，保障业务零中断。", detail: "多层防御架构，单点失效不影响整体防护效果", step: "03" },
    { icon: "ri-time-line", label: "应急响应", value: "<30min", title: "极速应急响应", desc: "专属安全专家团队随时待命，30分钟内响应安全事件，2小时内完成初步处置，最大限度降低损失。", detail: "配备专属客户成功经理，全程跟进处置进展", step: "04" },
  ];
  const defaultValueSummary = [
    { icon: "ri-shield-star-line", title: "降低安全风险 90%+", desc: "通过主动防御与持续监控，将企业遭受网络攻击的风险降低90%以上，保障业务连续性与数据安全。" },
    { icon: "ri-file-list-3-line", title: "合规一步到位", desc: "覆盖等保2.0、GDPR、行业监管要求，提供完整合规整改方案，避免监管处罚与声誉损失，合规成本降低60%。" },
    { icon: "ri-money-cny-circle-line", title: "安全成本降低 40%", desc: "托管安全服务模式，无需自建安全团队，综合安全运营成本降低40%，让企业专注核心业务发展。" },
  ];

  const items = advMainCards && advMainCards.length > 0
    ? advMainCards.map((c: any, i: number) => ({
        icon: c.icon || defaultItems[i]?.icon || "ri-shield-check-line",
        label: c.direction || "",
        value: c.metric || "",
        title: c.direction || "",
        desc: c.description || "",
        detail: c.supplement || "",
        step: String(i + 1).padStart(2, "0"),
      }))
    : defaultItems;

  const valueSummary = advBottomCards && advBottomCards.length > 0
    ? advBottomCards.map((c: any) => ({
        icon: c.icon || "ri-shield-check-line",
        title: c.metric || "",
        desc: c.description || "",
      }))
    : defaultValueSummary;
  return (
    <div>
      <SubSceneGridRow scenes={subs} accentColor={color} onNavigate={onNavigate} />

      {/* Intro with background image */}
      <IntroSection
        accentColor={color}
        title="全栈信息安全能力体系，从被动响应到主动防御"
        desc="面对日益复杂的网络威胁环境，企业需要的不仅是单点防护工具，而是一套覆盖网络边界、数据资产、云端业务的立体化防御体系。我们整合下一代防火墙、数据加密、云安全管控、威胁情报分析与7×24h安全运营服务，帮助企业构建从预防、检测到响应的全链路安全闭环，同时满足等保2.0、GDPR等合规要求，让安全真正成为业务增长的底座而非障碍。"
        bgImage="https://readdy.ai/api/search-image?query=cybersecurity%20digital%20defense%20abstract%20visualization%2C%20glowing%20network%20protection%20shield%2C%20dark%20navy%20background%20with%20teal%20circuit%20patterns%2C%20enterprise%20security%20operations%20center%20atmosphere%2C%20professional%20technology%2C%20no%20people&width=1200&height=600&seq=sec_intro_bg01&orientation=landscape"
        highlights={[
          { icon: "ri-shield-flash-line", text: "网络边界防护" },
          { icon: "ri-lock-2-line", text: "数据加密治理" },
          { icon: "ri-cloud-line", text: "云安全管控" },
          { icon: "ri-radar-line", text: "威胁情报分析" },
          { icon: "ri-customer-service-2-line", text: "安全运营服务" },
          { icon: "ri-file-list-3-line", text: "等保合规整改" },
          { icon: "ri-search-eye-line", text: "渗透测试评估" },
          { icon: "ri-alarm-warning-line", text: "应急响应处置" },
        ]}
        stats={[
          { value: "99.9%", label: "威胁拦截率" },
          { value: "<30min", label: "应急响应时效" },
          { value: "三级", label: "等保认证覆盖" },
          { value: "7×24h", label: "安全运营监控" },
        ]}
      />

      {/* Architecture（优先使用后台配置） */}
      {(() => {
        const customArch = parseArchFromAdvantage(advantageData, apiProducts);
        if (customArch) {
          return (
            <ArchSection
              accentColor={color}
              archTitle={customArch.title || "信息安全防御架构"}
              archTiers={customArch.tiers}
            />
          );
        }
        return (
          <ArchSection
            accentColor={color}
            archTitle="信息安全防御架构"
            archTiers={[
              {
                label: "主动防御层",
                items: [
                  { icon: "ri-shield-flash-line", name: "网络安全", desc: "下一代防火墙、IDS/IPS、DDoS防护、WAF，构建多层次边界防线" },
                  { icon: "ri-radar-line", name: "威胁情报", desc: "实时威胁情报分析，主动发现并阻断已知与未知攻击" },
                ],
              },
              {
                label: "数据保护层",
                items: [
                  { icon: "ri-lock-2-line", name: "数据安全", desc: "数据分级分类、加密保护、DLP防泄漏、数据库审计" },
                  { icon: "ri-cloud-line", name: "云上安全", desc: "云WAF、CASB访问代理、CSPM云资产态势管理" },
                  { icon: "ri-computer-line", name: "终端安全", desc: "防病毒、EDR检测响应、终端行为管控与合规基线" },
                ],
              },
              {
                label: "运营保障层",
                items: [
                  { icon: "ri-dashboard-3-line", name: "安全运营（SOC）", desc: "7×24h监控，SIEM日志分析，SOAR自动化响应编排" },
                  { icon: "ri-customer-service-2-line", name: "信息安全服务", desc: "渗透测试、等保合规咨询、应急响应与溯源分析" },
                ],
              },
            ]}
          />
        );
      })()}

      {/* Big Advantages */}
      <AdvantagesSection
        accentColor={color}
        sectionTitle={advTitle}
        sectionSubtitle={advSubtitle}
        items={items}
        valueSummary={valueSummary}
      />

      <DynamicCasesRow cases={apiCases.filter((c: any) => c.solution === "info-security").slice(0, 6)} accentColor={color} categoryId="info-security" />
    </div>
  );
}

function InfraPanel({ onNavigate, apiSubSolutions, apiCases, apiProducts, advantageData }: PanelProps) {
  const color = "#0891b2";
  const subs = apiSubSolutions.length > 0 ? apiSubSolutions : [...infraSubScenes];

  const advTitle = advantageData?.title || "为什么选择我们的基础架构方案";
  const advSubtitle = advantageData?.subtitle || "从底层硬件到上层云平台，完整交付，全程护航";
  const advMainCards = advantageData?.mainCards;
  const advBottomCards = advantageData?.bottomCards;

  const defaultItems = [
    { icon: "ri-cpu-line", label: "硬件选型", value: "多品牌", title: "多品牌生态合作", desc: "与华为、Dell、HPE、联想等主流厂商深度合作，按需推荐最优性价比硬件配置，避免单一厂商锁定风险。", detail: "合作品牌 20+，覆盖服务器、存储、网络全品类", step: "01" },
    { icon: "ri-draft-line", label: "方案设计", value: "架构师", title: "资深架构师团队", desc: "拥有10年以上经验的资深架构师团队深度参与，输出可落地的详细技术方案，提供多套方案对比与成本优化建议。", detail: "架构师团队平均从业经验 12 年，持有多项厂商认证", step: "02" },
    { icon: "ri-tools-line", label: "工程实施", value: "认证工程师", title: "专业工程实施", desc: "认证工程师现场部署，严格遵循厂商最佳实践规范，项目按时交付率98%，验收一次通过率95%以上。", detail: "工程师持有 HCIE/CCIE/VCP 等顶级认证", step: "03" },
    { icon: "ri-customer-service-2-line", label: "运维保障", value: "7×24h", title: "全周期运维保障", desc: "7×24h监控告警，定期健康巡检，快速响应故障处置，提供年度IT健康报告，持续优化系统性能。", detail: "平均故障响应时间 15 分钟，年度系统可用性 99.99%", step: "04" },
  ];
  const defaultValueSummary = [
    { icon: "ri-speed-up-line", title: "业务效率提升 5x", desc: "超融合与云化架构将资源交付周期从数周缩短至分钟级，IT响应速度提升5倍以上，加速业务创新。" },
    { icon: "ri-safe-2-line", title: "业务连续性 99.99%", desc: "容灾备份与高可用架构确保核心系统99.99%可用性，RPO/RTO双零目标，业务零中断，数据零丢失。" },
    { icon: "ri-line-chart-line", title: "IT成本降低 30-50%", desc: "按需扩展的云化架构避免资源浪费，统一运维管理降低人力成本，综合IT基础设施成本降低30%-50%。" },
  ];

  const items = advMainCards && advMainCards.length > 0
    ? advMainCards.map((c: any, i: number) => ({
        icon: c.icon || defaultItems[i]?.icon || "ri-server-line",
        label: c.direction || "",
        value: c.metric || "",
        title: c.direction || "",
        desc: c.description || "",
        detail: c.supplement || "",
        step: String(i + 1).padStart(2, "0"),
      }))
    : defaultItems;

  const valueSummary = advBottomCards && advBottomCards.length > 0
    ? advBottomCards.map((c: any) => ({
        icon: c.icon || "ri-shield-check-line",
        title: c.metric || "",
        desc: c.description || "",
      }))
    : defaultValueSummary;

  return (
    <div>
      <SubSceneGridRow scenes={subs} accentColor={color} onNavigate={onNavigate} />

      <IntroSection
        accentColor={color}
        title="全栈基础架构交付体系，构建高可用数字基础设施"
        desc="从硬件选型到云平台建设，我们提供覆盖超融合、桌面云、存储、托管云、容灾备份、混合云、网络建设七大领域的完整解决方案。无论是初创企业的IT基础建设，还是大型集团的数字化转型，我们都能提供量身定制的架构方案，帮助企业构建高可用、弹性扩展、安全可靠的数字化基础设施，将资源交付周期从数周缩短至分钟级，IT响应速度提升5倍以上，综合基础设施成本降低30%-50%。"
        bgImage="https://readdy.ai/api/search-image?query=modern%20enterprise%20data%20center%20infrastructure%2C%20server%20racks%20with%20blue%20LED%20lighting%2C%20organized%20cable%20management%2C%20professional%20IT%20facility%2C%20dark%20background%20with%20teal%20accent%20lighting%2C%20no%20people&width=1200&height=600&seq=infra_intro_bg01&orientation=landscape"
        highlights={[
          { icon: "ri-stack-line", text: "超融合平台" },
          { icon: "ri-computer-line", text: "桌面云VDI" },
          { icon: "ri-hard-drive-2-line", text: "全闪/分布式存储" },
          { icon: "ri-upload-cloud-2-line", text: "托管云服务" },
          { icon: "ri-refresh-line", text: "容灾备份" },
          { icon: "ri-git-merge-line", text: "混合云架构" },
          { icon: "ri-router-line", text: "SD-WAN网络" },
          { icon: "ri-tools-line", text: "全周期运维" },
        ]}
        stats={[
          { value: "99.99%", label: "系统可用性" },
          { value: "分钟级", label: "资源交付速度" },
          { value: "50%↓", label: "综合IT成本" },
          { value: "7×24h", label: "运维保障" },
        ]}
      />

      {/* Architecture（优先使用后台配置） */}
      {(() => {
        const customArch = parseArchFromAdvantage(advantageData, apiProducts);
        if (customArch) {
          return (
            <ArchSection
              accentColor={color}
              archTitle={customArch.title || "基础架构方案体系"}
              archTiers={customArch.tiers}
            />
          );
        }
        return (
          <ArchSection
            accentColor={color}
            archTitle="基础架构方案体系"
            archTiers={[
              {
                label: "核心层",
                items: [
                  { icon: "ri-git-merge-line", name: "同架构混合云解决方案", desc: "公私云统一管理，工作负载弹性调度，数据安全流转，打破云孤岛" },
                  { icon: "ri-upload-cloud-2-line", name: "托管云解决方案", desc: "T3+数据中心专属托管，7×24h专业运维，降低企业IT管理负担" },
                ],
              },
              {
                label: "支撑层",
                items: [
                  { icon: "ri-stack-line", name: "超融合解决方案", desc: "计算存储网络三合一，即插即用，线性扩展，简化IT架构" },
                  { icon: "ri-hard-drive-2-line", name: "存储解决方案", desc: "全闪存储阵列与分布式存储并行，覆盖高性能与海量存储场景" },
                  { icon: "ri-router-line", name: "网络建设解决方案", desc: "SD-WAN广域网优化、园区核心网络、数据中心网络全栈交付" },
                ],
              },
              {
                label: "保障层",
                items: [
                  { icon: "ri-refresh-line", name: "容灾备份解决方案", desc: "同城双活、异地灾备多模式，RPO/RTO双零目标，业务连续性保障" },
                  { icon: "ri-computer-line", name: "桌面云解决方案", desc: "VDI虚拟桌面，数据不落地，任意终端安全接入，集中统一管控" },
                ],
              },
            ]}
          />
        );
      })()}

      <AdvantagesSection
        accentColor={color}
        sectionTitle={advTitle}
        sectionSubtitle={advSubtitle}
        items={items}
        valueSummary={valueSummary}
      />

      <DynamicCasesRow cases={apiCases.filter((c: any) => c.solution === "infrastructure").slice(0, 6)} accentColor={color} categoryId="infrastructure" />
    </div>
  );
}

function SmartPanel({ onNavigate, apiSubSolutions, apiCases, apiProducts, advantageData }: PanelProps) {
  const color = "#059669";
  const subs = apiSubSolutions.length > 0 ? apiSubSolutions : smartSubScenes;

  const advTitle = advantageData?.title || "为什么选择我们的弱电智能化方案";
  const advSubtitle = advantageData?.subtitle || "一站式交付，从设计到运维，全程专业团队负责";
  const advMainCards = advantageData?.mainCards;
  const advBottomCards = advantageData?.bottomCards;

  const defaultItems = [
    { icon: "ri-search-eye-line", label: "需求调研", value: "深度洞察", title: "专业需求调研", desc: "深入了解业务场景与痛点，现场勘察与访谈相结合，输出专业需求分析报告，确保方案精准匹配实际需求。", detail: "调研报告涵盖现状评估、需求清单、风险识别三大模块", step: "01" },
    { icon: "ri-draft-line", label: "方案设计", value: "量身定制", title: "量身定制方案", desc: "资深工程师团队量身定制系统架构与技术选型，提供可落地的详细方案，包含施工图纸、设备清单与实施计划。", detail: "方案设计通过率 100%，客户满意度 98%", step: "02" },
    { icon: "ri-tools-line", label: "工程施工", value: "规范作业", title: "专业工程施工", desc: "专业施工团队规范作业，严格把控工程质量与进度，全程项目经理跟进，确保按时按质完成交付。", detail: "工程师持有弱电系统集成资质，施工规范符合国家标准", step: "03" },
    { icon: "ri-customer-service-2-line", label: "运维保障", value: "7×24h", title: "长期运维保障", desc: "7×24h响应支持，定期巡检维护，保障系统长期稳定运行，提供年度系统健康报告与升级建议。", detail: "平均响应时间 30 分钟，年度系统可用性 99.9%", step: "04" },
  ];
  const defaultValueSummary = [
    { icon: "ri-building-2-line", title: "打造智慧空间", desc: "将传统物理空间升级为智能互联环境，提升园区安全性、运营效率与员工体验，实现空间价值最大化。" },
    { icon: "ri-plug-line", title: "系统深度集成", desc: "打通安防、门禁、会议、网络等多个子系统，统一管理平台，消除信息孤岛，管理效率提升60%以上。" },
    { icon: "ri-leaf-line", title: "节能降耗 20-35%", desc: "智能楼控与能源管理系统，实现园区能耗降低20%-35%，绿色可持续运营，助力企业ESG目标达成。" },
  ];

  const items = advMainCards && advMainCards.length > 0
    ? advMainCards.map((c: any, i: number) => ({
        icon: c.icon || defaultItems[i]?.icon || "ri-building-line",
        label: c.direction || "",
        value: c.metric || "",
        title: c.direction || "",
        desc: c.description || "",
        detail: c.supplement || "",
        step: String(i + 1).padStart(2, "0"),
      }))
    : defaultItems;

  const valueSummary = advBottomCards && advBottomCards.length > 0
    ? advBottomCards.map((c: any) => ({
        icon: c.icon || "ri-shield-check-line",
        title: c.metric || "",
        desc: c.description || "",
      }))
    : defaultValueSummary;
  return (
    <div>
      <SubSceneGridRow scenes={subs} accentColor={color} onNavigate={onNavigate} />

      <IntroSection
        accentColor={color}
        title="一站式弱电智能化交付，打造未来智慧空间"
        desc="以场景为核心，提供从需求调研、方案设计、工程施工到系统调试、运维保障的全流程服务。覆盖园区智能化、智能会议、数据中心建设三大核心场景，打通安防、门禁、会议、网络等多个子系统，实现统一管控平台，消除信息孤岛。我们不只是设备供应商，更是企业智慧空间的长期合作伙伴，帮助企业将传统物理空间升级为安全、高效、节能的智能互联环境。"
        bgImage="https://readdy.ai/api/search-image?query=modern%20smart%20building%20intelligent%20campus%20exterior%2C%20building%20automation%20system%20visualization%2C%20professional%20corporate%20park%20with%20technology%20integration%2C%20clean%20architecture%20with%20subtle%20digital%20overlay%2C%20daytime%2C%20no%20people&width=1200&height=600&seq=smart_intro_bg01&orientation=landscape"
        highlights={[
          { icon: "ri-building-4-line", text: "园区综合布线" },
          { icon: "ri-camera-line", text: "视频监控系统" },
          { icon: "ri-door-lock-line", text: "人脸识别门禁" },
          { icon: "ri-presentation-line", text: "智能会议系统" },
          { icon: "ri-server-line", text: "数据中心建设" },
          { icon: "ri-settings-3-line", text: "楼宇自控系统" },
          { icon: "ri-wifi-line", text: "无线网络覆盖" },
          { icon: "ri-leaf-line", text: "能源管理系统" },
        ]}
        stats={[
          { value: "一站式", label: "全流程交付" },
          { value: "20-35%", label: "能耗降低" },
          { value: "T3+", label: "机房建设标准" },
          { value: "7×24h", label: "运维响应" },
        ]}
      />

      {/* Architecture（优先使用后台配置） */}
      {(() => {
        const customArch = parseArchFromAdvantage(advantageData, apiProducts);
        if (customArch) {
          return (
            <ArchSection
              accentColor={color}
              archTitle={customArch.title || "弱电智能化方案体系"}
              archTiers={customArch.tiers}
            />
          );
        }
        return (
          <ArchSection
            accentColor={color}
            archTitle="弱电智能化方案体系"
            archTiers={[
              {
                label: "空间智能层",
                items: [
                  { icon: "ri-building-4-line", name: "园区建设智能化解决方案", desc: "视频监控、人脸门禁、楼宇自控、综合布线、访客管理一体化集成" },
                  { icon: "ri-presentation-line", name: "智能化会议解决方案", desc: "4K会议一体机、无线投屏、会议室预约管理、远程视频协作" },
                ],
              },
              {
                label: "基础设施层",
                items: [
                  { icon: "ri-server-line", name: "数据中心建设解决方案", desc: "T3+机房规划设计、精密空调制冷、模块化UPS供配电、综合布线全生命周期" },
                ],
              },
            ]}
          />
        );
      })()}

      <AdvantagesSection
        accentColor={color}
        sectionTitle={advTitle}
        sectionSubtitle={advSubtitle}
        items={items}
        valueSummary={valueSummary}
      />

      <DynamicCasesRow cases={apiCases.filter((c: any) => c.solution === "smart-integration").slice(0, 6)} accentColor={color} categoryId="smart-integration" />
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function SolutionsBlocks({ blockRefs, activeTab, onTabChange }: Props) {
  const navigate = useNavigate();
  const activeTabConfig = TABS.find((t) => t.id === activeTab)!;
  const sectionRef = useRef<HTMLDivElement>(null);
  const tabBarRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [isSticky, setIsSticky] = useState(false);
  const [tabBarHeight, setTabBarHeight] = useState(0);
  const [solutionTree, setSolutionTree] = useState<any[]>([]);
  const [apiCases, setApiCases] = useState<any[]>([]);
  const [apiProducts, setApiProducts] = useState<{ id: number; name: string }[]>([]);
  const [advantageMap, setAdvantageMap] = useState<Record<string, any>>({});

  useEffect(() => {
    getSolutionTree().then((res: any) => setSolutionTree(Array.isArray(res) ? res : [])).catch(() => {});
    getCases({ pageNum: 1, pageSize: 100 }).then((res: any) => {
      const rows = res.rows ?? res.data ?? (Array.isArray(res) ? res : []);
      setApiCases(rows.map((c: any) => ({ ...c, tags: c.tags ?? [], metrics: c.metrics ?? [], summary: c.summary ?? c.description ?? "" })));
    }).catch(() => {});
    getProducts().then((list: any) => {
      const arr = Array.isArray(list) ? list : [];
      setApiProducts(arr.map((p: any) => ({ id: Number(p.id), name: p.name })));
    }).catch(() => {});
    // 加载三个分类的核心优势配置
    const categories = ["info-security", "it-infrastructure", "intelligent-building"];
    categories.forEach((cat) => {
      getCategoryAdvantage(cat).then((res: any) => {
        const data = res?.data ?? res;
        // 只要后端返回任一字段（title/subtitle/mainCards/bottomCards/arch），就把 data 存入 map
        if (data && (data.title || data.subtitle || data.arch || (data.mainCards && data.mainCards.length) || (data.bottomCards && data.bottomCards.length))) {
          setAdvantageMap((prev) => ({ ...prev, [cat]: data }));
        }
      }).catch(() => {});
    });
  }, []);

  useEffect(() => {
    const tabEl = tabBarRef.current;
    if (!tabEl) return;
    const NAVBAR_H = 72;
    const check = () => {
      const rect = tabEl.getBoundingClientRect();
      setIsSticky(rect.bottom <= NAVBAR_H);
      setTabBarHeight(tabEl.offsetHeight);
    };
    check();
    window.addEventListener("scroll", check, { passive: true });
    window.addEventListener("resize", check, { passive: true });
    return () => {
      window.removeEventListener("scroll", check);
      window.removeEventListener("resize", check);
    };
  }, []);

  const handleTabChange = (id: TabId) => {
    onTabChange(id);
    setTimeout(() => {
      if (sectionRef.current) {
        const NAVBAR_H = 72;
        const top = sectionRef.current.getBoundingClientRect().top + window.scrollY - NAVBAR_H;
        window.scrollTo({ top, behavior: "smooth" });
      }
    }, 50);
  };

  const handleNavigateToProduct = (sceneId: string) => {
    navigate(`/solutions/detail/${sceneId}`);
  };

  void tabBarHeight;

  const TabBarContent = () => (
    <div className="flex flex-col sm:flex-row gap-2 p-1.5 bg-gray-50 rounded-2xl border border-gray-100">
      {TABS.map((tab) => {
        const isActive = activeTab === tab.id;
        return (
          <button
            key={tab.id}
            onClick={() => handleTabChange(tab.id)}
            className="flex-1 flex items-center justify-center gap-2.5 px-5 py-3.5 rounded-xl transition-all duration-300 cursor-pointer whitespace-nowrap"
            style={
              isActive
                ? { backgroundColor: tab.color, color: "#fff" }
                : { backgroundColor: "transparent", color: "#6b7280" }
            }
          >
            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
              <i className={`${tab.icon} text-base`}></i>
            </div>
            <span className="text-sm font-bold">{tab.label}</span>
            {isActive && (
              <div className="w-4 h-4 flex items-center justify-center ml-0.5">
                <i className="ri-check-line text-white text-xs"></i>
              </div>
            )}
          </button>
        );
      })}
    </div>
  );

  return (
    <div
      className="bg-[#f9fafb]"
      ref={(el) => {
        (sectionRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
        (blockRefs["info-security"] as React.MutableRefObject<HTMLDivElement | null>).current = el;
        (blockRefs["it-infrastructure"] as React.MutableRefObject<HTMLDivElement | null>).current = el;
        (blockRefs["intelligent-building"] as React.MutableRefObject<HTMLDivElement | null>).current = el;
      }}
      id="solutions-blocks"
    >
      {/* Sticky tab bar */}
      {isSticky && (
        <div
          className="fixed top-[72px] left-0 right-0 z-40 bg-white/95 backdrop-blur-sm border-b border-gray-100"
          style={{ boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}
        >
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-2.5">
            <TabBarContent />
          </div>
        </div>
      )}

      <section className="py-20 lg:py-28">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">

          {/* Section header */}
          <div className="text-center mb-10">
            <p className="text-xs font-semibold tracking-widest text-gray-400 uppercase mb-3">三大核心方案</p>
            <h2 className="text-3xl lg:text-4xl font-bold text-[#0d1f3c] mb-4">
              选择适合您的
              <span style={{ color: activeTabConfig.color }}> 解决方案</span>
            </h2>
            <p className="text-gray-500 text-sm max-w-xl mx-auto leading-relaxed">
              点击下方标签切换方案，点击子场景卡片可直接跳转查看相关产品
            </p>
          </div>

          {/* Inline tab bar */}
          <div ref={tabBarRef} className="mb-10">
            <TabBarContent />
          </div>

          {/* Panel content */}
          <div ref={contentRef}>
            <div key={activeTab}>
              {(() => {
                // 从解决方案树中提取当前分类的子方案
                const cat = solutionTree.find((c: any) => (c.categoryId || c.id) === activeTab);
                const apiSubs: SubScene[] = (cat?.subSolutions || []).map((sub: any) => ({
                  id: String(sub.id),
                  icon: sub.icon || "",
                  name: sub.name || "",
                  desc: sub.description || sub.desc || "",
                  // 卡片标签取自方案「基础信息-标签」；无标签时回退到特性名称
                  tags: (Array.isArray(sub.tags) && sub.tags.length > 0)
                    ? sub.tags.filter(Boolean)
                    : (sub.features || []).map((f: any) => f.name).filter(Boolean),
                  productCategory: String(sub.id),
                  image: sub.image || "",
                }));
                const panelProps = { onNavigate: handleNavigateToProduct, apiSubSolutions: apiSubs, apiCases, apiProducts };
                return (
                  <>
                    {activeTab === "info-security" && <SecurityPanel {...panelProps} advantageData={advantageMap["info-security"] ?? null} />}
                    {activeTab === "it-infrastructure" && <InfraPanel {...panelProps} advantageData={advantageMap["it-infrastructure"] ?? null} />}
                    {activeTab === "intelligent-building" && <SmartPanel {...panelProps} advantageData={advantageMap["intelligent-building"] ?? null} />}
                  </>
                );
              })()}
            </div>
          </div>

        </div>
      </section>
    </div>
  );
}
